import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Building2, Loader2 } from "lucide-react";

export default function Login() {
  const { login, user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (loading) return null;
  if (user) {
    const commerceRoles = ["vendeur", "manager_commercial"];
    const adminRoles = ["secretaire_bo", "responsable_facturation"];
    if (commerceRoles.includes(user.role)) return <Navigate to="/commerce" replace />;
    if (adminRoles.includes(user.role)) return <Navigate to="/admin" replace />;
    return <Navigate to="/commerce" replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const data = await login(email.trim(), password.trim());
      const commerceRoles = ["vendeur", "manager_commercial"];
      const adminRoles = ["secretaire_bo", "responsable_facturation"];
      if (commerceRoles.includes(data.role)) navigate("/commerce");
      else if (adminRoles.includes(data.role)) navigate("/admin");
      else navigate("/commerce");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Connexion échouée");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex login-bg">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="bg-white p-10 rounded-sm shadow-xl border border-slate-200">
            <div className="flex items-center gap-3 mb-8">
              <Building2 className="w-8 h-8 text-[#111111]" strokeWidth={1.5} />
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Le Kiosque Immobilier</div>
                <h1 className="text-2xl font-bold text-[#111111]" style={{fontFamily:"Cabinet Grotesk"}}>CRM + BackOffice</h1>
              </div>
            </div>

            <p className="text-sm text-slate-600 mb-6">Bienvenue. Connectez-vous pour accéder à votre espace.</p>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Identifiant</label>
                <input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  autoComplete="username"
                  data-testid="login-email-input"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#111111] focus:border-[#111111] text-sm"
                  placeholder="adminjohn ou vous@agence.fr"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Mot de passe</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  autoComplete="current-password"
                  data-testid="login-password-input"
                  className="w-full px-3 py-2.5 border border-slate-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#111111] focus:border-[#111111] text-sm"
                  placeholder="••••••••"
                />
              </div>

              {error && (
                <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm" data-testid="login-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="login-submit-button"
                className="w-full bg-[#111111] text-white py-2.5 rounded-sm font-semibold text-sm uppercase tracking-wider hover:bg-slate-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                Se connecter
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
